import {
  useBuild,
  useJobsForStep,
} from "app/components/Playground/BuildContext";
import { StateIcon } from "app/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "app/components/Playground/Node/colors";
import Emojify from "app/components/shared/Emojify";
import { isParallelCommandStep, Step } from "app/lib/pipeline";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { JobListItem } from "./JobListItem";
import { PropsWithChildren, useEffect } from "react";
import { useLocation } from "react-router-dom";
import BuildPageStore from "app/stores/BuildPageStore";

export function createStepListItem({ step }: { step: Step }) {
  if (step.type === "group") {
    return (
      <GroupListItem
        key={step.uuid}
        step={step}
        data-testid="GroupStepListItem"
      >
        {step.steps?.map((step) => createStepListItem({ step }))}
      </GroupListItem>
    );
  } else if (isParallelCommandStep(step)) {
    return (
      <GroupListItem
        key={step.uuid}
        step={step}
        data-testid="ParallelCommandStepListItem"
      >
        <StepListItem step={step} />
      </GroupListItem>
    );
  }
  return (
    <StepListItem key={step.uuid} step={step} data-testid="StepListItem" />
  );
}

interface Props {
  step: Step;
  "data-testid"?: string;
}

/**
 * Render a grouped step with its child steps.
 */
export function GroupListItem({
  step,
  children,
  ...props
}: PropsWithChildren<Props>) {
  return (
    <div
      {...props}
      key={step.uuid}
      className="flex flex-col border border-gray-500 px-3 mb-3 rounded-[3px]"
    >
      <div>
        <h3 className="text-md m-0 py-3.5 flex gap-1.5">
          <StateIcon
            {...step}
            className={twMerge(textColorForStep(step), "h-[14px] w-[14px]")}
          />
          <Emojify text={step.label} />
        </h3>
      </div>
      {children}
    </div>
  );
}

/**
 * Render a single build step with its job(s).
 */
export function StepListItem({ step, ...props }: Props) {
  const { build } = useBuild();
  const { hash } = useLocation();

  if (!build) {
    return null;
  }

  const jobs = useJobsForStep(step.uuid).filter(
    (job) => job.state !== "broken",
  );

  useEffect(() => {
    if (jobs.length === 0) {
      return;
    }

    if (hash) {
      BuildPageStore.expandJob({ id: hash.slice(1) });
    } else if (jobs.length === 1) {
      const job = jobs[jobs.length - 1];
      BuildPageStore.expandJob(job);
    }
  }, [step.uuid, jobs.length]);

  return (
    <div {...props} className="job-list-pipeline">
      <div className={classNames({ "job-retry-group": jobs.length > 1 })}>
        {jobs.map((job, index) => (
          <JobListItem
            key={job.id}
            job={job}
            isRetry={index > 0}
            autoFollow={true}
          />
        ))}
      </div>
    </div>
  );
}
